import { convertCpuStringToNumber, convertMemoryStringToNumber } from "../../../../services/utils";

// Constants matching backend validation
export const RESOURCE_VALIDATION_CONSTANTS = {
  MIN_CPU_MILLICORES: 25,
  MIN_MEMORY_MIB: 32,
  DEFAULT_MEMORY_CPU_RATIO: 8,
  RELAXED_MEMORY_CPU_RATIO: 32,
  STATEFUL_MAX_CPU_RATIO: 4,
  STATEFUL_MAX_CPU_RANGE: 4000,
  STATEFUL_MAX_MEMORY_RATIO: 4,
  STATEFUL_MAX_MEMORY_RANGE: 4096,
} as const;

export type ResourceField = "minCpu" | "cpu" | "minMemory" | "memory";

export interface ResourceSuggestion {
  field: ResourceField;
  label: string;
  value: number;
  unit: string;
  displayValue: string;
}

export type ResourceValidationType =
  | "memory-cpu-ratio"
  | "stateful-cpu-ratio"
  | "stateful-cpu-range"
  | "stateful-memory-ratio"
  | "stateful-memory-range"
  | "gpu-cpu"
  | "gpu-memory"
  | "min-cpu"
  | "max-cpu"
  | "min-memory"
  | "max-memory"
  | "min-exceeds-max-cpu"
  | "min-exceeds-max-memory";

export interface ResourceValidationResult {
  type: ResourceValidationType;
  field: ResourceField;
  message: string;
  shortMessage: string;
  suggestions: ResourceSuggestion[];
}

export interface ResourceValidationInput {
  type: string;
  capacityAI: boolean;
  hasRelaxedRatioTag: boolean;
  minCpuAmount: string;
  minCpuUnit: string;
  maxCpuAmount: string;
  maxCpuUnit: string;
  minMemoryAmount: string;
  minMemoryUnit: string;
  maxMemoryAmount: string;
  maxMemoryUnit: string;
  gpuType?: string;
  gpuNvidiaModel?: string;
  gpuNvidiaQuantity?: string;
}

function formatCpuValue(millicores: number): { value: number; unit: string; displayValue: string } {
  if (millicores >= 1000 && millicores % 1000 === 0) {
    return { value: millicores / 1000, unit: "", displayValue: `${millicores / 1000} Cores` };
  }
  return { value: millicores, unit: "m", displayValue: `${millicores}m` };
}

function formatMemoryValue(mib: number): { value: number; unit: string; displayValue: string } {
  if (mib >= 1024 && mib % 1024 === 0) {
    return { value: mib / 1024, unit: "Gi", displayValue: `${mib / 1024}Gi` };
  }
  return { value: mib, unit: "Mi", displayValue: `${mib}Mi` };
}

// CPU format validation: millicores must be integers, cores can have up to 3 decimals
function isValidCpuFormat(amount: string, unit: string): boolean {
  if (!amount) return true;
  if (unit === "m") {
    // Millicores must be an integer
    return /^[0-9]+$/.test(amount);
  }
  // Cores can be integer or decimal with up to 3 decimal places
  return /^[0-9]+(\.[0-9]{1,3})?$/.test(amount);
}

// Memory format validation - matches backend: /^[0-9]+(\.[0-9]{1,3})?(G|M|k|Gi|Mi|Ki)?$/i
function isValidMemoryFormat(amount: string): boolean {
  if (!amount) return true;
  // Integer or decimal with up to 3 decimal places (all units allow decimals)
  return /^[0-9]+(\.[0-9]{1,3})?$/.test(amount);
}

export function validateContainerResources(input: ResourceValidationInput): ResourceValidationResult[] {
  const results: ResourceValidationResult[] = [];

  const hasMinValues = input.capacityAI || input.type === "stateful";
  const maxCpuLabel = hasMinValues ? "Max CPU" : "Reserved CPU";
  const maxMemoryLabel = hasMinValues ? "Max Memory" : "Reserved Memory";

  // Format validation for CPU
  if (input.maxCpuAmount && !isValidCpuFormat(input.maxCpuAmount, input.maxCpuUnit)) {
    results.push({
      type: "max-cpu",
      field: "cpu",
      message: input.maxCpuUnit === "m"
        ? `${maxCpuLabel} in millicores must be a whole number.`
        : `${maxCpuLabel} in cores must have at most 3 decimal places.`,
      shortMessage: input.maxCpuUnit === "m"
        ? `${maxCpuLabel} must be a whole number`
        : `${maxCpuLabel} invalid format`,
      suggestions: [],
    });
  }

  if (hasMinValues && input.minCpuAmount && !isValidCpuFormat(input.minCpuAmount, input.minCpuUnit)) {
    results.push({
      type: "min-cpu",
      field: "minCpu",
      message: input.minCpuUnit === "m"
        ? "Min CPU in millicores must be a whole number."
        : "Min CPU in cores must have at most 3 decimal places.",
      shortMessage: input.minCpuUnit === "m"
        ? "Min CPU must be a whole number"
        : "Min CPU invalid format",
      suggestions: [],
    });
  }

  // Format validation for Memory
  if (input.maxMemoryAmount && !isValidMemoryFormat(input.maxMemoryAmount)) {
    results.push({
      type: "max-memory",
      field: "memory",
      message: `${maxMemoryLabel} must be a number with at most 3 decimal places.`,
      shortMessage: `${maxMemoryLabel} invalid format`,
      suggestions: [],
    });
  }

  if (hasMinValues && input.minMemoryAmount && !isValidMemoryFormat(input.minMemoryAmount)) {
    results.push({
      type: "min-memory",
      field: "minMemory",
      message: "Min Memory must be a number with at most 3 decimal places.",
      shortMessage: "Min Memory invalid format",
      suggestions: [],
    });
  }

  // Parse values
  // Note: Use nullish coalescing (??) to preserve empty string for CPU unit (empty = cores, "m" = millicores)
  const minCpuString = `${input.minCpuAmount || 0}${input.minCpuUnit ?? "m"}`;
  const maxCpuString = `${input.maxCpuAmount || 0}${input.maxCpuUnit ?? "m"}`;
  const minMemoryString = `${input.minMemoryAmount || 0}${input.minMemoryUnit ?? "Mi"}`;
  const maxMemoryString = `${input.maxMemoryAmount || 0}${input.maxMemoryUnit ?? "Mi"}`;

  const minCpu = convertCpuStringToNumber(minCpuString);
  const maxCpu = convertCpuStringToNumber(maxCpuString);
  const minMemory = convertMemoryStringToNumber(minMemoryString);
  const maxMemory = convertMemoryStringToNumber(maxMemoryString);

  // Skip remaining validation if format errors exist or max values are not set
  if (results.length > 0 || (!maxCpu && !maxMemory)) {
    return results;
  }

  const { MIN_CPU_MILLICORES, MIN_MEMORY_MIB } = RESOURCE_VALIDATION_CONSTANTS;

  // 1. Max CPU minimum validation
  if (maxCpu && maxCpu < MIN_CPU_MILLICORES) {
    const suggestion = formatCpuValue(MIN_CPU_MILLICORES);
    results.push({
      type: "max-cpu",
      field: "cpu",
      message: `${maxCpuLabel} must be at least ${MIN_CPU_MILLICORES}m.`,
      shortMessage: `${maxCpuLabel} must be at least ${MIN_CPU_MILLICORES}m`,
      suggestions: [
        {
          field: "cpu",
          label: `Set ${maxCpuLabel} to ${suggestion.displayValue}`,
          ...suggestion,
        },
      ],
    });
  }

  // 2. Max Memory minimum validation
  if (maxMemory && maxMemory < MIN_MEMORY_MIB) {
    const suggestion = formatMemoryValue(MIN_MEMORY_MIB);
    results.push({
      type: "max-memory",
      field: "memory",
      message: `${maxMemoryLabel} must be at least ${MIN_MEMORY_MIB}Mi.`,
      shortMessage: `${maxMemoryLabel} must be at least ${MIN_MEMORY_MIB}Mi`,
      suggestions: [
        {
          field: "memory",
          label: `Set ${maxMemoryLabel} to ${suggestion.displayValue}`,
          ...suggestion,
        },
      ],
    });
  }

  // 3. Min CPU validation (if applicable)
  if (hasMinValues && minCpu) {
    if (minCpu < MIN_CPU_MILLICORES) {
      const suggestion = formatCpuValue(MIN_CPU_MILLICORES);
      results.push({
        type: "min-cpu",
        field: "minCpu",
        message: `Min CPU must be at least ${MIN_CPU_MILLICORES}m.`,
        shortMessage: `Min CPU must be at least ${MIN_CPU_MILLICORES}m`,
        suggestions: [
          {
            field: "minCpu",
            label: `Set Min CPU to ${suggestion.displayValue}`,
            ...suggestion,
          },
        ],
      });
    }

    if (maxCpu && minCpu > maxCpu) {
      const suggestion = formatCpuValue(maxCpu);
      results.push({
        type: "min-exceeds-max-cpu",
        field: "minCpu",
        message: `Min CPU cannot be greater than ${maxCpuLabel}.`,
        shortMessage: `Min CPU cannot exceed ${maxCpuLabel}`,
        suggestions: [
          {
            field: "minCpu",
            label: `Set Min CPU to ${suggestion.displayValue}`,
            ...suggestion,
          },
        ],
      });
    }
  }

  // 4. Min Memory validation (if applicable)
  if (hasMinValues && minMemory) {
    if (minMemory < MIN_MEMORY_MIB) {
      const suggestion = formatMemoryValue(MIN_MEMORY_MIB);
      results.push({
        type: "min-memory",
        field: "minMemory",
        message: `Min Memory must be at least ${MIN_MEMORY_MIB}Mi.`,
        shortMessage: `Min Memory must be at least ${MIN_MEMORY_MIB}Mi`,
        suggestions: [
          {
            field: "minMemory",
            label: `Set Min Memory to ${suggestion.displayValue}`,
            ...suggestion,
          },
        ],
      });
    }

    if (maxMemory && minMemory > maxMemory) {
      const suggestion = formatMemoryValue(maxMemory);
      results.push({
        type: "min-exceeds-max-memory",
        field: "minMemory",
        message: `Min Memory cannot be greater than ${maxMemoryLabel}.`,
        shortMessage: `Min Memory cannot exceed ${maxMemoryLabel}`,
        suggestions: [
          {
            field: "minMemory",
            label: `Set Min Memory to ${suggestion.displayValue}`,
            ...suggestion,
          },
        ],
      });
    }
  }

  // 5. Memory-to-CPU Ratio validation
  if (maxCpu && maxMemory) {
    const maxRatio = input.hasRelaxedRatioTag
      ? RESOURCE_VALIDATION_CONSTANTS.RELAXED_MEMORY_CPU_RATIO
      : RESOURCE_VALIDATION_CONSTANTS.DEFAULT_MEMORY_CPU_RATIO;
    const currentRatio = maxMemory / maxCpu;

    if (currentRatio > maxRatio) {
      const suggestedCpu = Math.ceil(maxMemory / maxRatio);
      const suggestedMemory = Math.floor(maxCpu * maxRatio);
      const cpuSuggestion = formatCpuValue(suggestedCpu);
      const memorySuggestion = formatMemoryValue(suggestedMemory);

      results.push({
        type: "memory-cpu-ratio",
        field: "cpu",
        message: `Memory to CPU ratio must be at most ${maxRatio}:1. Current ratio is ${currentRatio.toFixed(1)}:1.`,
        shortMessage: `Memory to CPU ratio exceeds ${maxRatio}:1`,
        suggestions: [
          {
            field: "cpu",
            label: `Increase ${maxCpuLabel} to ${cpuSuggestion.displayValue}`,
            ...cpuSuggestion,
          },
          {
            field: "memory",
            label: `Decrease ${maxMemoryLabel} to ${memorySuggestion.displayValue}`,
            ...memorySuggestion,
          },
        ],
      });
    }
  }

  // 6. Stateful CPU Ratio/Range validation
  if (input.type === "stateful" && minCpu && maxCpu) {
    const { STATEFUL_MAX_CPU_RATIO, STATEFUL_MAX_CPU_RANGE } = RESOURCE_VALIDATION_CONSTANTS;
    const cpuRatio = maxCpu / minCpu;
    const cpuRange = maxCpu - minCpu;

    if (cpuRatio > STATEFUL_MAX_CPU_RATIO) {
      const suggestedMax = minCpu * STATEFUL_MAX_CPU_RATIO;
      const suggestedMin = Math.ceil(maxCpu / STATEFUL_MAX_CPU_RATIO);
      const maxSuggestion = formatCpuValue(suggestedMax);
      const minSuggestion = formatCpuValue(suggestedMin);

      results.push({
        type: "stateful-cpu-ratio",
        field: "cpu",
        message: `For stateful workloads, the ratio between Max CPU and Min CPU must be at most ${STATEFUL_MAX_CPU_RATIO}:1. Current ratio is ${cpuRatio.toFixed(1)}:1.`,
        shortMessage: `Stateful CPU ratio exceeds ${STATEFUL_MAX_CPU_RATIO}:1`,
        suggestions: [
          {
            field: "minCpu",
            label: `Increase Min CPU to ${minSuggestion.displayValue}`,
            ...minSuggestion,
          },
          {
            field: "cpu",
            label: `Decrease Max CPU to ${maxSuggestion.displayValue}`,
            ...maxSuggestion,
          },
        ],
      });
    } else if (cpuRange > STATEFUL_MAX_CPU_RANGE) {
      const suggestedMax = minCpu + STATEFUL_MAX_CPU_RANGE;
      const suggestedMin = maxCpu - STATEFUL_MAX_CPU_RANGE;
      const maxSuggestion = formatCpuValue(suggestedMax);
      const minSuggestion = formatCpuValue(suggestedMin);

      results.push({
        type: "stateful-cpu-range",
        field: "cpu",
        message: `For stateful workloads, the difference between Max CPU and Min CPU must be at most ${STATEFUL_MAX_CPU_RANGE}m. Current difference is ${cpuRange}m.`,
        shortMessage: `Stateful CPU range exceeds ${STATEFUL_MAX_CPU_RANGE}m`,
        suggestions: [
          {
            field: "minCpu",
            label: `Increase Min CPU to ${minSuggestion.displayValue}`,
            ...minSuggestion,
          },
          {
            field: "cpu",
            label: `Decrease Max CPU to ${maxSuggestion.displayValue}`,
            ...maxSuggestion,
          },
        ],
      });
    }
  }

  // 7. Stateful Memory Ratio/Range validation
  if (input.type === "stateful" && minMemory && maxMemory) {
    const { STATEFUL_MAX_MEMORY_RATIO, STATEFUL_MAX_MEMORY_RANGE } = RESOURCE_VALIDATION_CONSTANTS;
    const memoryRatio = maxMemory / minMemory;
    const memoryRange = maxMemory - minMemory;

    if (memoryRatio > STATEFUL_MAX_MEMORY_RATIO) {
      const suggestedMax = minMemory * STATEFUL_MAX_MEMORY_RATIO;
      const suggestedMin = Math.ceil(maxMemory / STATEFUL_MAX_MEMORY_RATIO);
      const maxSuggestion = formatMemoryValue(suggestedMax);
      const minSuggestion = formatMemoryValue(suggestedMin);

      results.push({
        type: "stateful-memory-ratio",
        field: "memory",
        message: `For stateful workloads, the ratio between Max Memory and Min Memory must be at most ${STATEFUL_MAX_MEMORY_RATIO}:1. Current ratio is ${memoryRatio.toFixed(1)}:1.`,
        shortMessage: `Stateful memory ratio exceeds ${STATEFUL_MAX_MEMORY_RATIO}:1`,
        suggestions: [
          {
            field: "minMemory",
            label: `Increase Min Memory to ${minSuggestion.displayValue}`,
            ...minSuggestion,
          },
          {
            field: "memory",
            label: `Decrease Max Memory to ${maxSuggestion.displayValue}`,
            ...maxSuggestion,
          },
        ],
      });
    } else if (memoryRange > STATEFUL_MAX_MEMORY_RANGE) {
      const suggestedMax = minMemory + STATEFUL_MAX_MEMORY_RANGE;
      const suggestedMin = maxMemory - STATEFUL_MAX_MEMORY_RANGE;
      const maxSuggestion = formatMemoryValue(suggestedMax);
      const minSuggestion = formatMemoryValue(suggestedMin);

      results.push({
        type: "stateful-memory-range",
        field: "memory",
        message: `For stateful workloads, the difference between Max Memory and Min Memory must be at most ${STATEFUL_MAX_MEMORY_RANGE}Mi. Current difference is ${memoryRange}Mi.`,
        shortMessage: `Stateful memory range exceeds ${STATEFUL_MAX_MEMORY_RANGE}Mi`,
        suggestions: [
          {
            field: "minMemory",
            label: `Increase Min Memory to ${minSuggestion.displayValue}`,
            ...minSuggestion,
          },
          {
            field: "memory",
            label: `Decrease Max Memory to ${maxSuggestion.displayValue}`,
            ...maxSuggestion,
          },
        ],
      });
    }
  }

  // 8. GPU validation - only run if GPU type is nvidia
  const gpuNvidiaQuantity = Number(input.gpuNvidiaQuantity);
  const isNvidiaGpuEnabled = input.gpuType === "nvidia" && input.gpuNvidiaModel && !Number.isNaN(gpuNvidiaQuantity) && gpuNvidiaQuantity > 0;
  if (isNvidiaGpuEnabled) {
    const gpuNvidiaModel = input.gpuNvidiaModel!;

    let gpuMinCpu = 2000;
    let gpuMaxCpu = 8000 * gpuNvidiaQuantity;
    let gpuMinMemoryGb = 7 * gpuNvidiaQuantity;
    let gpuMaxMemoryGb = 31 * gpuNvidiaQuantity;

    if (gpuNvidiaModel === "t4") {
      // T4 requires higher minCpu when quantity > 1 OR memory > 14Gi (14336 MiB)
      if (gpuNvidiaQuantity > 1 || (maxMemory && maxMemory > 14336)) {
        gpuMinCpu = gpuNvidiaQuantity * 4000;
      }
    }

    if (gpuNvidiaModel === "a10g") {
      gpuMinCpu = 6000 * gpuNvidiaQuantity;
      gpuMinMemoryGb = 8;
      gpuMaxMemoryGb = 62;
    }

    const gpuMinMemory = gpuMinMemoryGb * 1024;
    const gpuMaxMemory = gpuMaxMemoryGb * 1024;

    // Check effective CPU (minCpu if set, otherwise maxCpu)
    const effectiveCpu = hasMinValues && minCpu ? minCpu : maxCpu;
    const cpuField: ResourceField = hasMinValues && minCpu ? "minCpu" : "cpu";
    const cpuLabel = cpuField === "minCpu" ? "Min CPU" : maxCpuLabel;

    if (effectiveCpu && effectiveCpu < gpuMinCpu) {
      const suggestion = formatCpuValue(gpuMinCpu);
      results.push({
        type: "gpu-cpu",
        field: cpuField,
        message: `GPU (${gpuNvidiaModel.toUpperCase()}) requires ${cpuLabel} to be at least ${gpuMinCpu}m.`,
        shortMessage: `GPU requires ${cpuLabel} >= ${gpuMinCpu}m`,
        suggestions: [
          {
            field: cpuField,
            label: `Set ${cpuLabel} to ${suggestion.displayValue}`,
            ...suggestion,
          },
        ],
      });
    } else if (effectiveCpu && effectiveCpu > gpuMaxCpu) {
      const suggestion = formatCpuValue(gpuMaxCpu);
      results.push({
        type: "gpu-cpu",
        field: cpuField,
        message: `GPU (${gpuNvidiaModel.toUpperCase()}) requires ${cpuLabel} to be at most ${gpuMaxCpu}m.`,
        shortMessage: `GPU requires ${cpuLabel} <= ${gpuMaxCpu}m`,
        suggestions: [
          {
            field: cpuField,
            label: `Set ${cpuLabel} to ${suggestion.displayValue}`,
            ...suggestion,
          },
        ],
      });
    }

    // Check effective Memory (minMemory if set, otherwise maxMemory)
    const effectiveMemory = hasMinValues && minMemory ? minMemory : maxMemory;
    const memoryField: ResourceField = hasMinValues && minMemory ? "minMemory" : "memory";
    const memoryLabel = memoryField === "minMemory" ? "Min Memory" : maxMemoryLabel;

    if (effectiveMemory && effectiveMemory < gpuMinMemory) {
      const suggestion = formatMemoryValue(gpuMinMemory);
      results.push({
        type: "gpu-memory",
        field: memoryField,
        message: `GPU (${gpuNvidiaModel.toUpperCase()}) requires ${memoryLabel} to be at least ${gpuMinMemoryGb}Gi (${gpuMinMemory}Mi).`,
        shortMessage: `GPU requires ${memoryLabel} >= ${gpuMinMemoryGb}Gi (${gpuMinMemory}Mi)`,
        suggestions: [
          {
            field: memoryField,
            label: `Set ${memoryLabel} to ${suggestion.displayValue}`,
            ...suggestion,
          },
        ],
      });
    } else if (effectiveMemory && effectiveMemory > gpuMaxMemory) {
      const suggestion = formatMemoryValue(gpuMaxMemory);
      results.push({
        type: "gpu-memory",
        field: memoryField,
        message: `GPU (${gpuNvidiaModel.toUpperCase()}) requires ${memoryLabel} to be at most ${gpuMaxMemoryGb}Gi (${gpuMaxMemory}Mi).`,
        shortMessage: `GPU requires ${memoryLabel} <= ${gpuMaxMemoryGb}Gi (${gpuMaxMemory}Mi)`,
        suggestions: [
          {
            field: memoryField,
            label: `Set ${memoryLabel} to ${suggestion.displayValue}`,
            ...suggestion,
          },
        ],
      });
    }
  }

  return results;
}

/**
 * Maps a validation result to the form field path that should show the error
 */
export function getFieldPathForValidation(containerIndex: number, validation: ResourceValidationResult): string {
  const fieldMap: Record<ResourceField, string> = {
    minCpu: `containers.${containerIndex}.minCpu.amount`,
    cpu: `containers.${containerIndex}.cpu.amount`,
    minMemory: `containers.${containerIndex}.minMemory.amount`,
    memory: `containers.${containerIndex}.memory.amount`,
  };
  return fieldMap[validation.field];
}

/**
 * Returns all resource field paths for a container
 */
export function getResourceFieldPaths(containerIndex: number): string[] {
  return [
    `containers.${containerIndex}.minCpu.amount`,
    `containers.${containerIndex}.cpu.amount`,
    `containers.${containerIndex}.minMemory.amount`,
    `containers.${containerIndex}.memory.amount`,
  ];
}
